import React, { useEffect } from 'react';
import _ from 'lodash';
import {
  StepChangeDirection,
  MultipleStepsAnswer,
} from '@typings/ResidentTypes';
import { ErrorsProvider, useErrors } from '@contexts/ErrorsContext';
import { FormError, SubmitStatusEnum } from '@typings/common';
import {
  formatDate,
  isValidEmail,
  updateErrors,
  validatePastDateMessage,
} from '@utils/commons';
import NewErrorSummary from '@components/NewErrorSummary';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';
import PlanUpdatedDate from './components/PlanUpdatedDate';
import AcpFurtherInformation from './components/AcpFurtherInformation';
import UploadAdvanceCarePlan from './components/UploadAdvanceCarePlan';
import CareStatus from './components/CareStatus';
import PreviousAdvanceCarePlan from './components/PreviousAdvanceCarePlan';
import RoomNumberOrName from './components/RoomNumberOrName';
import ResidentFurtherInfo from './components/ResidentFurtherInfo';
import LegalCarePlanningForms from './components/legal-care-planning-form';
import LPA from './components/lpa';
import useStepTwoForm from './useStepTwoForm';
import { stepTwofieldMappings, StepTwoProps } from './utils';
import { SuccessNotiPositionEnum } from '../commons';
import './styles.scss';

const StepTwoComponent = ({
  slug,
  changeStep,
  userId,
  threeStepsData,
  residentFullName,
  setIsFormChanged,
  onSubmitThreeStepsData,
  handleGetResidentDetails,
  callApiStatus,
  showSuccessNotification,
  setShowSuccessNotification,
}: StepTwoProps) => {
  const {
    careStatus,
    setCareStatus,
    furtherCareStatus,
    setFurtherCareStatus,
    roomNumberOrName,
    setRoomNumberOrName,
    advanceCarePlan,
    setAdvanceCarePlan,
    acpFurtherInformation,
    setAcpFurtherInformation,
    residentFurtherInformation,
    setResidentFurtherInformation,
    fileList,
    setFileList,
    lpa,
    setLpa,
    handleChangeCareStatus,
    lpaFromData,
    planUpdatedDate,
    setPlanUpdatedDate,
    advanceDecision,
    setAdvanceDecision,
    cprDecision,
    setCprDecision,
    acmp,
    setAcmp,
  } = useStepTwoForm({ slug, threeStepsData, setIsFormChanged });
  const { errors, setErrors } = useErrors();
  const successMessage = {
    title: 'Your progress has been saved',
    content: 'Please continue below',
  };

  const onFinish = (e: any, position: SuccessNotiPositionEnum) => {
    e.preventDefault();

    let validationErrors: FormError[] = [];

    const planUpdatedDateErrMessage = validatePastDateMessage(planUpdatedDate);
    validationErrors = updateErrors(
      validationErrors,
      'planUpdatedDate',
      planUpdatedDateErrMessage
    );

    const adrtUpdatedDateErrMessage = validatePastDateMessage(
      advanceDecision.updatedDate
    );
    validationErrors = updateErrors(
      validationErrors,
      'adrtUpdatedDate',
      adrtUpdatedDateErrMessage
    );

    const acmpUpdatedDateErrMessage = validatePastDateMessage(acmp.updatedDate);
    validationErrors = updateErrors(
      validationErrors,
      'acmpUpdatedDate',
      acmpUpdatedDateErrMessage
    );

    validationErrors = updateErrors(
      validationErrors,
      'lpaContacEmail',
      !isValidEmail(lpa.lpaInfo.lpaContact.email)
        ? 'Please enter a valid email'
        : null
    );

    setErrors(validationErrors);

    if (validationErrors.length !== 0) {
      return;
    }

    const submitValues: any = Object.entries({
      careStatus,
      furtherCareStatus,
      roomNumberOrName,
      advanceCarePlan,
      planUpdatedDate: formatDate(planUpdatedDate, 'YYYY-MM-DD'),
      acpDocuments: fileList as MultipleStepsAnswer,
      advanceDecision: {
        ...advanceDecision,
        updatedDate: formatDate(advanceDecision.updatedDate, 'YYYY-MM-DD'),
      },
      cprDecision,
      acmp: {
        ...acmp,
        updatedDate: formatDate(acmp.updatedDate, 'YYYY-MM-DD'),
      },
      acpFurtherInformation,
      residentFurtherInformation,
      healthAndWelfareLPAStatus: lpa.healthAndWelfareLPAStatus,
      lpaLocation: lpa.lpaInfo.lpaLocation,
      lpaDeputies: lpa.lpaInfo.lpaDeputies,
      lpaFullName: lpa.lpaInfo.lpaContact.fullName,
      lpaEmail: lpa.lpaInfo.lpaContact.email ?? '',
      lpaPhoneNumber: lpa.lpaInfo.lpaContact.phoneNumber ?? '',
      lpaRelationship: lpa.lpaInfo.lpaContact.relationship ?? '',
      uploadedLpaFileList: lpa.lpaInfo
        .uploadedLpaFileList as MultipleStepsAnswer,
    }).map(([key, answer]) => ({
      key,
      question:
        stepTwofieldMappings[key as keyof typeof stepTwofieldMappings] || key,
      answer,
    }));

    if (careStatus) {
      handleChangeCareStatus();
    }

    onSubmitThreeStepsData({ step2: submitValues }, position);
  };

  useEffect(() => {
    handleGetResidentDetails();
  }, [handleGetResidentDetails]);

  const showSaveButton = (position: SuccessNotiPositionEnum) => {
    return (
      <button
        type="submit"
        className="govuk-button"
        data-module="govuk-button"
        onClick={e => {
          onFinish(e, position);
        }}
        disabled={showSuccessNotification.btnLoading}
      >
        Save and continue
      </button>
    );
  };

  const onSaveInLegalCarePlanning = (
    e: any,
    position: SuccessNotiPositionEnum
  ) => {
    onFinish(e, position);
  };

  const onSaveInLpa = (e: any, position: SuccessNotiPositionEnum) => {
    onFinish(e, position);
  };

  return (
    <div className="step2">
      <div className="mt-5 md:mt-10">
        <h1 className="govuk-heading-xl">
          {residentFullName
            ? `${residentFullName} - Required Information`
            : 'Required Information'}
        </h1>
        {errors.length > 0 && <NewErrorSummary errors={errors} />}
        <form>
          <CareStatus
            residentFullName={residentFullName}
            careStatus={careStatus}
            setCareStatus={setCareStatus}
            furtherCareStatus={furtherCareStatus}
            setFurtherCareStatus={setFurtherCareStatus}
          />

          {showSaveButton(SuccessNotiPositionEnum.FIRST)}
          {showSuccessNotification.status &&
            showSuccessNotification.position ===
              SuccessNotiPositionEnum.FIRST && (
              <SuccessNotificationBanner
                title={successMessage.title}
                content={successMessage.content}
                duration={3000}
                noScrollToTop
              />
            )}
          <RoomNumberOrName
            residentFullName={residentFullName}
            roomNumberOrName={roomNumberOrName}
            setRoomNumberOrName={setRoomNumberOrName}
          />
          <PreviousAdvanceCarePlan
            advanceCarePlan={advanceCarePlan}
            setAdvanceCarePlan={setAdvanceCarePlan}
            residentFullName={residentFullName}
          />
          <AcpFurtherInformation
            acpFurtherInformation={acpFurtherInformation}
            setAcpFurtherInformation={setAcpFurtherInformation}
          />
          {showSaveButton(SuccessNotiPositionEnum.SECOND)}
          {showSuccessNotification.status &&
            showSuccessNotification.position ===
              SuccessNotiPositionEnum.SECOND && (
              <SuccessNotificationBanner
                title={successMessage.title}
                content={successMessage.content}
                duration={3000}
                noScrollToTop
              />
            )}
          <UploadAdvanceCarePlan
            userId={userId}
            setFileList={setFileList}
            fileList={fileList}
          />
          <PlanUpdatedDate
            planUpdatedDate={planUpdatedDate}
            setPlanUpdatedDate={setPlanUpdatedDate}
          />
          <LegalCarePlanningForms
            userId={userId}
            residentFullName={residentFullName}
            advanceDecision={advanceDecision}
            setAdvanceDecision={setAdvanceDecision}
            cprDecision={cprDecision}
            setCprDecision={setCprDecision}
            acmp={acmp}
            setAcmp={setAcmp}
            onSaveInLegalCarePlanning={onSaveInLegalCarePlanning}
            showSuccessNotification={showSuccessNotification}
          />
          <LPA
            userId={userId}
            lpa={lpa}
            setLpa={setLpa}
            lpaFromData={lpaFromData}
            showSuccessNotification={showSuccessNotification}
            onSaveInLpa={onSaveInLpa}
          />
          <ResidentFurtherInfo
            residentFullName={residentFullName}
            residentFurtherInformation={residentFurtherInformation}
            setResidentFurtherInformation={setResidentFurtherInformation}
          />
          <div className="flex items-center justify-between w-full mt-10 govuk-bp:mt-14 gap-5">
            <div className="govuk-button-group justify-between">
              <button
                type="submit"
                disabled={callApiStatus.loading}
                className="govuk-button govuk-button--secondary"
                onClick={() => changeStep(StepChangeDirection.Previous)}
              >
                Previous
              </button>

              <div className="w-full govuk-bp:w-fit">
                <button
                  type="submit"
                  className="govuk-button"
                  data-module="govuk-button"
                  disabled={callApiStatus.loading}
                  onClick={e => {
                    onFinish(e, SuccessNotiPositionEnum.LAST);
                  }}
                >
                  Save
                </button>
                <button
                  type="submit"
                  className="govuk-button"
                  data-module="govuk-button"
                  disabled={callApiStatus.loading}
                  onClick={() => changeStep(StepChangeDirection.Next)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
          {callApiStatus.submitted === SubmitStatusEnum.SUCCESS && (
            <SuccessNotificationBanner
              title="This document has been saved."
              content={callApiStatus.message}
              duration={3000}
              noScrollToTop
            />
          )}
        </form>
      </div>
    </div>
  );
};

const StepTwo: React.FC<StepTwoProps> = props => {
  return (
    <ErrorsProvider>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <StepTwoComponent {...props} />
    </ErrorsProvider>
  );
};
export default StepTwo;
